/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { cssMap, jsx } from '@atlaskit/css';

import { startConstellationCanvas } from './canvas';
import { useConstellationPaintStyle } from './color';
import type { ConstellationProps } from './types';

const styles = cssMap({
	canvas: {
		display: 'block',
		height: '100%',
		width: '100%',
	},
});

/**
 * Decorative Canvas renderer for the Enterprise HER constellation.
 *
 * The host owns sizing and interaction semantics. This component bridges
 * React lifecycle and current render props into the imperative Canvas renderer.
 */
export function Constellation(props: ConstellationProps): JSX.Element {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const paintStyle = useConstellationPaintStyle();
	const renderStateRef = useRef({ paintStyle, props });

	renderStateRef.current = { paintStyle, props };

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		return startConstellationCanvas(canvas, () => renderStateRef.current);
	}, []);

	return (
		<canvas
			aria-hidden="true"
			css={styles.canvas}
			data-testid="rovo-voice-constellation"
			ref={canvasRef}
			tabIndex={-1}
		/>
	);
}
